// forcedReadQueue.mjs — SB-146 站内信强读闸的纯逻辑（第一百二十二批）。
// 无 React、无 DOM：排序 / 到底判定 / 倒计时 / 重查节流，全部输入化（SB-042 惯例，
// 判定离开 JSX 才有判据）。组件（ForcedReadGate.jsx）只是薄壳。
//
// ── 这道闸在管什么（SB-146 §2 裁决 1/3）────────────────────────────────────
// 「必须送达」的通道此前没有任何东西保证「被读到」：未读积压是静默的，排队机制
// 因此转不动。这里的每只函数对应闸上一个可判定的环节：
//   · buildForcedQueue —— 谁该被拦、按什么顺序（旧→新：先欠的先还，排队机制
//     也是按已读事件逐封放行的，顺序错了它就转不动）；
//   · isAtBottom —— 「读到底了」的判定，**短正文不许卡死**（裁决明文：内容不满
//     一屏视同已到底——一封两行的信没有任何东西可滚，闸若等一个永不发生的
//     scroll 事件，用户被永久锁死，那比不读还坏）；
//   · startCountdown / mayConfirmRead —— 到底后读秒，读完才**许人确认**；
//   · drainRounds —— 一轮读完不等于未读清空（列表查询有 limit），拉到 0 条才放行。
//
// ── 需求方 2026-08-20 裁：推翻 SB-146 裁① ──────────────────────────────────
// 原裁「倒计时归零即自动标已读并自动前进」。新形态：读秒只是**下限**，归零后亮出
// 「确认已读」由人点。所以这里的门改名 mayConfirmRead——它答的是「**可以确认**了
// 吗」，不再是「该标已读了吗」。四个条件一字不改（判据仍在），变的只是门后面那只
// 手：从 effect 换成了人。名字必须跟着语义走，否则下一个读这行的人会以为
// 「返回 true = 已经标了」。
import { normalizeMessages } from '../pages/messages.mjs';

// 到底判定的像素容差。浏览器在缩放/高 DPI 下 scrollTop 是小数，
// scrollTop+clientHeight 与 scrollHeight 可以永远差 1px 上下——逐像素判定不留
// 容差的闸在 125% 缩放下就是一道永远到不了底的墙。
export const SCROLL_BOTTOM_TOLERANCE_PX = 2;

// 到底之后的读秒。这是「读完这封的最短用时」的下限，不是放行本身：读秒归零只让
// 「确认已读」变得可点（2026-08-20 裁）。文案里的秒数一律从这里插值——手写一个 3
// 进 i18n，改常量的那天文案就成了假话（SB-151 记过一次账，本批还清）。
export const READ_COUNTDOWN_SECONDS = 3;

// 路由切换触发的未读重查的短 TTL。落地查一次 + 每次路由切换查一次是裁决 3 的
// 要求，但快速连点导航不该变成一串并发查询——铃铛已有的 60s 轮询也不动。
export const UNREAD_RECHECK_TTL_MS = 15_000;

// ── 强读队列：未读收件信，旧→新 ────────────────────────────────────────────
// 「未读」= 收件方向 且 state === 'sent_unread' 且 readAtMs 空。三条同时咬：
// 服务端将来若在收件箱里放出别的形状，这里的失败方向是**少拦一封**，
// 而不是把一封已读的信再拦一遍（那是把人锁在他已经还清的账前面）。
// 输入是 GET /api/me/messages?box=inbox 的原始响应；形状收敛复用 normalizeMessages
// ——第二份 normalize 就是第二个会漂的形状。
export function buildForcedQueue(data) {
  const rows = normalizeMessages(data).filter(
    (r) => r.direction === 'in' && r.state === 'sent_unread' && r.readAtMs == null,
  );
  // 后端列表是 id DESC（新的在前）；强读要旧→新（§3.4）。排序键取发出时刻
  // （收件箱里恒有 sentAtMs；缺了回落 createdAtMs），同刻按数值 id 升序钉稳。
  return rows.slice().sort((a, b) => {
    const ta = a.sentAtMs ?? a.createdAtMs ?? 0;
    const tb = b.sentAtMs ?? b.createdAtMs ?? 0;
    if (ta !== tb) return ta - tb;
    return Number(a.id) - Number(b.id);
  });
}

// ── 到底判定 ────────────────────────────────────────────────────────────────
// metrics = { scrollTop, clientHeight, scrollHeight }（组件从滚动容器上读，
// 这里只算不量——量不进单测）。
// **内容不满一屏视同已到底**（SB-146 §3.4 明文）：scrollHeight ≤ clientHeight+容差
// 时没有任何滚动可发生，等 scroll 事件等于把短信收件人永久锁死。
// 量不出来（容器还没挂上、字段缺失）⇒ false：fail-closed 的方向是闸**留着**，
// 绝不因为量具坏了就放行。
export function isAtBottom(metrics, tolerancePx = SCROLL_BOTTOM_TOLERANCE_PX) {
  const ch = Number(metrics?.clientHeight);
  const sh = Number(metrics?.scrollHeight);
  if (!Number.isFinite(ch) || !Number.isFinite(sh)) return false;
  // 0/0 是「什么都没显示」的退化量测（Dialog 过渡帧、容器未完成布局），不是短正文
  // ——证伪评审实测它此前被「不满一屏」分支放行，与量不出同罪：闸留着。
  if (ch <= 0 || sh <= 0) return false;
  if (sh <= ch + tolerancePx) return true;          // 不满一屏 ⇒ 视同已到底
  const st = Number(metrics?.scrollTop);
  if (!Number.isFinite(st)) return false;
  return st + ch >= sh - tolerancePx;               // 逐像素判定，留容差
}

// 倒计时推进：只会往 0 走、到 0 停住。脏输入按 0 收（0 ⇒ mayMarkRead 才放行，
// 看似「脏输入直接放行」，但 remain 只由 startCountdown 喂，脏值进不来；
// 收成 0 是为了让 NaN 不把倒计时冻成永远走不完）。
export function countdownNext(remain) {
  const n = Number(remain);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n - 1);
}

// 「可以确认已读」的门：**四个条件都要**——到底（严格 true，不收 truthy）、
// 倒计时走完（严格 0；null = 还没开始数）、且**正文真的呈现过这一封**
//（peekId 按信 id 键控）。返回 true 只让确认键可点，**不标任何东西**：标已读由
// 人点那一下发起（2026-08-20 裁），门与手是两件事。
// 后两参是证伪评审那条 must-fix 的治本：advanceQueue 之后的一帧里 bottom/remain
// 还是上一封的旧闭包值，React 状态重置晚一拍——只看前两个条件，每逢真读完一封，
// 下一封的确认键会在正文上屏前就亮着（自动标已读的年代里那是当场静默标掉：
// 服务端落已读、撤回窗口关死、排队放行，而正文从未上屏；改成手动后它退化成
// 「一次误点就能跳过一封」，同样是闸上的洞）。
// peekId===curId 不依赖任何重置时序：新一封的 peek 尚未返回时它必不相等。
export function mayConfirmRead(atBottom, remain, peekId, curId) {
  return atBottom === true && remain === 0
    && peekId != null && curId != null && peekId === curId;
}

// ── 倒计时驱动器 ────────────────────────────────────────────────────────────
// timer 参数化（默认真实 globalThis）：测试给假计时器，组件与测试走同一条时间轴
// ——否则「3 秒门」这个判据只能靠真睡 3 秒或根本不测。
// onTick 先收满值（界面立刻显示 3，而不是空一秒），到 0 自动停。返回取消函数
// （换下一封信时上一封的读秒必须停，两个读秒并行会让下一封提前放行）。
export function startCountdown(onTick, timer = globalThis, seconds = READ_COUNTDOWN_SECONDS) {
  let remain = seconds;
  onTick(remain);
  const h = timer.setInterval(() => {
    remain = countdownNext(remain);
    onTick(remain);
    if (remain <= 0) timer.clearInterval(h);
  }, 1000);
  return () => timer.clearInterval(h);
}

// 重查节流：上次查询还没过 TTL 就不再查（防路由连点风暴）。没查过（lastMs 非数）
// ⇒ 必须查：漏查的方向是漏拦，而这道闸存在的全部意义就是不漏。
export function shouldRecheck(nowMs, lastMs, ttlMs = UNREAD_RECHECK_TTL_MS) {
  // ⚠ Number(null) 是 0：null 直接交给 Number 会把「没查过」误读成「1970 年查过」
  // ——虽然结论碰巧同为 true，但那是碰巧，不是判定（messages.mjs 的 num 同一条教训）。
  if (lastMs === null || lastMs === undefined) return true;
  const last = Number(lastMs);
  if (!Number.isFinite(last)) return true;
  return Number(nowMs) - last >= ttlMs;
}

// ── 穷尽读：一轮读完 ≠ 未读清空 ─────────────────────────────────────────────
// 闸的前提是「未读**全部**读完才放行」，可它读的是 GET /api/me/messages?limit=50
// 这一次**有上限**的查询。未读多于一页时，「本轮队列走完」只说明这一页读完了
// ——此前闸在那一刻直接 setPhase('idle')，于是第 51 封起被静默宣布不存在：一道
// 自称保证送达的闸，按页数打了折，而且不报错、不留痕，用户与发信人都以为读完了。
//
// ⚠ 「再拉一次就必然收敛」这条直觉在**收件箱**上是错的，写这段时当场证伪过：
// list_inbox 是 `WHERE to_account=? AND sent_at_ms IS NOT NULL ORDER BY id DESC
// LIMIT ?`（message_store.cpp）——它取的是**最新 N 行，不分已读未读**。读过的信
// 不会离开这个窗口。所以 53 封未读、窗口 50 的那个例子里：第一轮 50 封读完，第二
// 轮拉回来的还是同样那 50 行（只是都成了已读）、本页零未读，闸照旧放行，第 51-53
// 封依然没人读——**光加一层重拉修不掉它**。
// （铃铛那条链没有这个病：notifications?unread=true 是服务端过滤，读掉的真会离开。）
//
// 两条一起才成立：
//  ① 「本页零未读」不足以放行——先问**服务端数出来的**未读总数（unreadRemaining，
//    取同一份响应里的 unreadCount，不额外发一次请求）；
//  ② 本页零未读而服务端还欠着 ⇒ 未读落在窗口之外 ⇒ 把窗口开到服务端允许的最大
//    （needsWiderWindow / FORCED_INBOX_MAX_LIMIT）再问一次。
// 于是放行的条件成了「窗口开到最大也取不到任何未读」。收敛靠的是**每一轮至少有
// 一封被读掉**（人点了确认才推进），未读总数有限且逐轮递减；新到的信本就该拦
// ——那是闸的职责，不是不收敛。
export const FORCED_INBOX_PAGE_LIMIT = 50;

// 服务端允许的最大窗口：后端把 limit 钳在 1..100（routes_messages.cpp）。
// 第一百三十一批后端长出 `before` 键集游标、第一百七十八批闸接上它——开到最大
// 仍够不着的未读（**已读行不离窗**，压在第 101 行外的旧未读单靠开窗永远够不着）
// 现在沿游标逐页下潜取齐，101+ 天花板就此拆掉（SB-159 §2.1 那行、SB-146 关单
// 记的 (b) 尾账，同此了结）。
export const FORCED_INBOX_MAX_LIMIT = 100;

// 游标下潜的防御性页数护栏：服务端计数与行集矛盾（unreadCount 说欠着、翻到底也
// 找不到）时不许无限翻页。触顶即以手上所得返回——欠账由下一轮重拉兜住，
// 绝不静默当清空。50 页 × 100 行 = 5000 封的深度，真实收件箱够不到它。
export const FORCED_CURSOR_MAX_PAGES = 50;

// 服务端数出来的未读总数（GET /api/me/messages 的 unreadCount，与铃铛同源）。
// null = 字段缺席/脏值 ⇒ 「不知道」，与「0 封」不是一回事：老后端没有这个字段时，
// 闸退回按本页判断（少拦，不多拦），而不是把 undefined 当成 0 宣布清空。
export function unreadRemaining(data) {
  const n = Number(data?.unreadCount);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
}

// 该把窗口开大再问一次吗：本页一封未读都没有，服务端却说还欠着。
// 手上还有未读 ⇒ 先读完手上的（把窗口开大只会把同一批信再排一遍）。
export function needsWiderWindow(data, queueLen) {
  if (queueLen !== 0) return false;
  return (unreadRemaining(data) ?? 0) > 0;
}

// 拉一轮未读：常规窗口 → 没拿齐则开最大窗 → 仍没拿齐则沿键集游标逐页下潜，
// 直到拿齐服务端宣称的未读数、翻到底、或触到页数护栏。
// fetchInbox: (limit, before?) => Promise<响应体>（组件传 api.messages，测试传假的
// ——两处走同一条取数编排，「加宽」与「下潜」两步才有判据）。
// unreadCount 缺席（老后端）⇒ 按本页判断（少拦不多拦，口径不变）。
// 跨页的行**聚齐后统一建队**：buildForcedQueue 逐页排序，直接拼接会把
// 「旧→新」的全局序拼断（第二页整页都比第一页旧）。
export async function fetchForcedRound(fetchInbox) {
  const first = await fetchInbox(FORCED_INBOX_PAGE_LIMIT);
  const owed = unreadRemaining(first);
  const firstQ = buildForcedQueue(first);
  if (owed == null || firstQ.length >= owed) return firstQ;
  let data = await fetchInbox(FORCED_INBOX_MAX_LIMIT);
  let rows = Array.isArray(data?.messages) ? data.messages : [];
  let all = rows;
  let pages = 0;
  while (buildForcedQueue({ messages: all }).length < owed
         && data?.hasMore === true && rows.length > 0
         && pages < FORCED_CURSOR_MAX_PAGES) {
    const before = rows[rows.length - 1]?.id;
    if (before == null) break;
    // eslint-disable-next-line no-await-in-loop
    data = await fetchInbox(FORCED_INBOX_MAX_LIMIT, before);
    rows = Array.isArray(data?.messages) ? data.messages : [];
    all = all.concat(rows);
    pages += 1;
  }
  return buildForcedQueue({ messages: all });
}

// 穷尽驱动。fetchRound: () => Promise<item[]>；runRound: (items) => Promise<void|false>。
//   · 拉回 0 条（或非数组/脏值）⇒ 这一轮取数已尽全力仍无未读，收工放行；
//   · runRound 返回 false ⇒ 调用方**主动中止**（清欠段的「暂缓」出口）——与「拉回
//     0 条」是截然不同的两件事：前者欠账还在，绝不许被当成清完而弹「恢复自由」。
// 组件里 runRound 的 promise 由**人**逐封点完才 resolve，测试里是个假的：同一条
// 循环两处共用，收敛保证只有一份（判定活在 JSX 里 = 删掉它全套测试照样绿，
// 第一百零八批 R1 的教训）。
export async function drainRounds(fetchRound, runRound) {
  let rounds = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const items = await fetchRound();
    rounds += 1;
    if (!Array.isArray(items) || items.length === 0) return { rounds, stopped: false };
    // eslint-disable-next-line no-await-in-loop
    const go = await runRound(items);
    if (go === false) return { rounds, stopped: true };
  }
}
